import { Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Badge } from "../components/ui/Badge";
import { UnpublishedBadge } from "../features/campaigns/UnpublishedBadge";
import { Button } from "../components/ui/Button";
import { Dialog } from "../components/ui/Dialog";
import { EmptyState, ErrorState, Skeleton } from "../components/ui/Feedback";
import { Input, Select } from "../components/ui/Input";
import { TBody, TD, TH, THead, TR, Table } from "../components/ui/Table";
import { useToast } from "../components/ui/Toast";
import {
	DEFAULT_CAMPAIGN_TYPE,
	campaignTypeFor,
	campaignTypeGroups,
} from "../features/campaigns/campaignTypes";
import { useCampaigns, useCreateCampaign } from "../features/campaigns/useCampaigns";
import { useShopTimeZone } from "../features/settings/useSettings";
import { formatDate, titleCase } from "../lib/format";
import type { CampaignStatus, CampaignType } from "../lib/types";

const SOLID_STATUSES = new Set<CampaignStatus>(["active"]);

export const CampaignsPage = () => {
	const campaigns = useCampaigns();
	const createCampaign = useCreateCampaign();
	const navigate = useNavigate();
	const toast = useToast();
	const { timeZone } = useShopTimeZone();
	const [title, setTitle] = useState("");
	const [type, setType] = useState<CampaignType>(DEFAULT_CAMPAIGN_TYPE);
	const [createOpen, setCreateOpen] = useState(false);

	const create = () => {
		const trimmed = title.trim();
		if (!trimmed) return;

		createCampaign.mutate(
			{ title: trimmed, type },
			{
				onSuccess: (data) => {
					toast.push(`Created "${data.campaign.title}"`);
					setTitle("");
					setType(DEFAULT_CAMPAIGN_TYPE);
					setCreateOpen(false);
					void navigate(`/campaigns/${data.campaign.id}`);
				},
				onError: (error) => toast.push(error.message, "error"),
			},
		);
	};

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h2 className="text-xl font-semibold tracking-tight text-ink">Campaigns</h2>
					<p className="mt-1 text-sm text-ink-muted">
						Configurable offers applied to your storefront.
					</p>
				</div>
				<Button onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4" strokeWidth={2} />
					Create campaign
				</Button>
			</div>

			<Dialog
				open={createOpen}
				onOpenChange={(open) => {
					setCreateOpen(open);
					if (!open) setTitle("");
				}}
				title="Create campaign"
				description="Pick what kind of offer this is. Schedule, rules, and gifts come next."
				footer={
					<>
						<Button variant="outline" onClick={() => setCreateOpen(false)}>
							Cancel
						</Button>
						<Button onClick={create} disabled={!title.trim() || createCampaign.isPending}>
							{createCampaign.isPending ? "Creating…" : "Create"}
						</Button>
					</>
				}
			>
				<div className="space-y-4">
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">Campaign name</span>
						<Input
							autoFocus
							value={title}
							placeholder="Summer Free Gifts"
							onChange={(event) => setTitle(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") create();
							}}
						/>
					</label>

					<label className="block space-y-1.5">
						<span className="text-sm text-ink">Type</span>
						<Select value={type} onChange={(event) => setType(event.target.value as CampaignType)}>
							{campaignTypeGroups().map(({ group, types }) => (
								<optgroup key={group} label={group}>
									{types.map((entry) => (
										<option key={entry.type} value={entry.type}>
											{entry.label}
										</option>
									))}
								</optgroup>
							))}
						</Select>
						<span className="block text-xs text-ink-muted">
							{campaignTypeFor(type)?.description}
						</span>
					</label>
				</div>
			</Dialog>

			{campaigns.isError ? (
				<ErrorState title="Could not load campaigns" description={campaigns.error.message} />
			) : null}

			{campaigns.isPending ? <Skeleton className="h-40 w-full" /> : null}

			{campaigns.data && campaigns.data.items.length === 0 ? (
				<EmptyState
					title="No campaigns yet"
					description="Create one above to start configuring free gifts."
				/>
			) : null}

			{campaigns.data && campaigns.data.items.length > 0 ? (
				<div className="overflow-hidden rounded-md border border-line">
					<Table>
						<THead>
							<TR>
								<TH>Campaign</TH>
								<TH className="w-28">Status</TH>
								<TH className="w-32">Stacking</TH>
								<TH className="w-48">Starts</TH>
								<TH className="w-48">Ends</TH>
							</TR>
						</THead>
						<TBody>
							{campaigns.data.items.map((campaign) => (
								<TR key={campaign.id} className="hover:bg-raised">
									<TD>
										<Link
											to={`/campaigns/${campaign.id}`}
											className="font-medium text-ink underline-offset-4 hover:underline"
										>
											{campaign.title}
										</Link>
										<p className="mt-0.5 text-xs text-ink-faint">
											{campaignTypeFor(campaign.type)?.label ?? campaign.type}
										</p>
									</TD>
									<TD>
										<div className="flex flex-wrap items-center gap-1.5">
											<Badge variant={SOLID_STATUSES.has(campaign.status) ? "solid" : "outline"}>
												{titleCase(campaign.status)}
											</Badge>
											<UnpublishedBadge campaign={campaign} />
											{campaign.themeVisibility === "preview" ? (
												<Badge variant="outline">Preview</Badge>
											) : null}
										</div>
									</TD>
									<TD className="text-ink-muted">
										{campaign.stacking === "all" ? "All tiers" : "Highest only"}
									</TD>
									<TD className="text-xs text-ink-muted">
										{formatDate(campaign.startsAt, timeZone)}
									</TD>
									<TD className="text-xs text-ink-muted">
										{formatDate(campaign.endsAt, timeZone)}
									</TD>
								</TR>
							))}
						</TBody>
					</Table>
				</div>
			) : null}
		</div>
	);
};
